import { useSelector } from "react-redux";
import { FaChevronDown, FaChevronRight, FaFolder, FaFolderOpen } from "react-icons/fa6";
import ActionPopupMenu from "../Common/ActionPopupMenu";

export const countTotalItems = (node) => {
  const childFolders = node.children?.length ?? 0;
  const files = node.files?.length ?? 0;
  const nested = (node.children ?? []).reduce((sum, child) => sum + countTotalItems(child), 0);
  return childFolders + files + nested;
};

const FolderTreeNode = ({
  node,
  depth = 0,
  activeFolderId,
  onSelectFolder,
  onRenameFolder,
  onDeleteFolder,
  onManageMembers,
  expandedFolderIds,
  toggleFolderExpanded,
  isSorted = false,
  permissions = { isView: true, isSave: true, isDelete: true },
  activeNodeUploadsMap = null, // Map<nodeId, count> from React Query cache for correct file+folder badges
  enableDragMove = false,
  draggingMoveItem = null,
  moveDropTargetId = null,
  onMoveDragStart,
  onMoveDragEnd,
  onMoveDragOverFolder,
  onMoveDropOnFolder,
}) => {
  const { user } = useSelector((state) => state.root.auth);
  const nodeId = node.folderDNSID ?? node.folderID ?? node.id;
  const nodeName = node.folderName ?? node.name;
  const isExpanded = expandedFolderIds.has(nodeId);
  const hasChildren = node.children?.length > 0;
  const isActive =
    activeFolderId !== undefined &&
    activeFolderId !== null &&
    activeFolderId === nodeId;

  const rawCount = countTotalItems(node);
  // Use cached uploads count (files + folders) when this folder has been visited,
  // otherwise fall back to the recursive tree-only count (folders only).
  const totalItems = activeNodeUploadsMap?.has(nodeId)
    ? activeNodeUploadsMap.get(nodeId)
    : rawCount;

  const sortedChildren =
    isSorted && node.children
      ? [...node.children].sort((a, b) => {
        const nameA = (a.folderName ?? a.name ?? "").toLowerCase();
        const nameB = (b.folderName ?? b.name ?? "").toLowerCase();
        return nameA.localeCompare(nameB);
      })
      : node.children;

  const isMoveDropTarget =
    moveDropTargetId !== null &&
    moveDropTargetId !== undefined &&
    Number(moveDropTargetId) === Number(nodeId);
  const isDraggingThis =
    draggingMoveItem?.type === "Folder" &&
    Number(draggingMoveItem?.id) === Number(nodeId);

  const folderMenuItems = [
    {
      icon: <span className="text-lg">📂</span>,
      label: "Open",
      onClick: () => onSelectFolder(node),
    },
    permissions?.isSave && {
      icon: <span className="text-lg">✏️</span>,
      label: "Rename",
      onClick: () => onRenameFolder(nodeId, nodeName, "Folder"),
    },
    permissions?.isSave && {
      icon: <span className="text-lg">↔️</span>,
      label: "Move",
      onClick: () => {
        window.dispatchEvent(
          new CustomEvent("openMoveModal", {
            detail: { id: nodeId, name: nodeName, type: "Folder", isCopy: false },
          })
        );
      },
    },
    permissions?.isSave && {
      icon: <span className="text-lg">📋</span>,
      label: "Copy",
      onClick: () => {
        window.dispatchEvent(
          new CustomEvent("openMoveModal", {
            detail: { id: nodeId, name: nodeName, type: "Folder", isCopy: true },
          })
        );
      },
    },
    // && user?.userID === node?.userID
    (user?.parentID === 0) && {
      icon: <span className="text-lg">👥</span>,
      label: "Manage Members",
      onClick: () => onManageMembers(node),
    },
    permissions?.isDelete && { divider: true },
    permissions?.isDelete && {
      icon: <span className="text-lg">🗑️</span>,
      label: "Delete",
      isDelete: true,
      onClick: () => onDeleteFolder(nodeId, nodeName, "Folder"),
    },
  ].filter(Boolean);

  return (
    <div>
      <div
        className={`group flex items-center gap-1 pr-2 py-2 cursor-pointer rounded-md transition-colors ${isMoveDropTarget
          ? "dns-move-drop-target ring-2 ring-indigo-400 ring-inset bg-indigo-50/80"
          : isActive
            ? "bg-indigo-50 text-indigo-700 font-semibold"
            : "hover:bg-slate-50 text-slate-700"
          } ${isDraggingThis ? "opacity-50" : ""}`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        draggable={enableDragMove && permissions?.isSave}
        onDragStart={(e) => onMoveDragStart?.(e, { id: nodeId, name: nodeName, type: "Folder" })}
        onDragEnd={() => onMoveDragEnd?.()}
        onDragOver={(e) => onMoveDragOverFolder?.(e, nodeId)}
        onDrop={(e) => onMoveDropOnFolder?.(e, nodeId)}
        onClick={() => {
          if (hasChildren) toggleFolderExpanded(nodeId);
          onSelectFolder(node);
        }}
      >
        {/* Expand/collapse chevron */}
        <span className="w-3.5 text-slate-400 shrink-0">
          {hasChildren ? (
            isExpanded ? <FaChevronDown size={9} /> : <FaChevronRight size={9} />
          ) : null}
        </span>

        {/* Folder icon */}
        {isExpanded && hasChildren ? (
          <FaFolderOpen size={13} className="text-indigo-400 shrink-0" />
        ) : (
          <FaFolder
            size={13}
            className={`${isActive ? "text-indigo-500" : "text-slate-400"} shrink-0`}
          />
        )}

        {/* Folder name */}
        <span
          className={`text-[14px] font-medium flex-1 truncate ${isActive ? "text-indigo-700 font-semibold" : "text-slate-700"}`}
          data-tooltip-id="toolbar-tooltip"
          data-tooltip-content={nodeName}
        >
          {nodeName}
        </span>

        {/* Item count badge — visible on hover or when active */}
        {totalItems > 0 && (
          <span
            className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 border border-indigo rounded-md leading-none
              ${isActive
                ? " border-indigo-200 bg-indigo-100 text-indigo-600"
                : " bg-slate-100 text-slate-500"
              }`}
          >
            {totalItems}
          </span>
        )}

        {/* Context menu */}
        {folderMenuItems.length > 0 && (
          <ActionPopupMenu
            placement="right-start"
            alwaysShowTrigger={isActive}
            className="w-52"
            items={folderMenuItems}
          />
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {sortedChildren.map((child) => (
            <FolderTreeNode
              key={child.folderDNSID ?? child.folderID ?? child.id}
              node={child}
              depth={depth + 1}
              activeFolderId={activeFolderId}
              onSelectFolder={onSelectFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onManageMembers={onManageMembers}
              expandedFolderIds={expandedFolderIds}
              toggleFolderExpanded={toggleFolderExpanded}
              isSorted={isSorted}
              permissions={permissions}
              activeNodeUploadsMap={activeNodeUploadsMap}
              enableDragMove={enableDragMove}
              draggingMoveItem={draggingMoveItem}
              moveDropTargetId={moveDropTargetId}
              onMoveDragStart={onMoveDragStart}
              onMoveDragEnd={onMoveDragEnd}
              onMoveDragOverFolder={onMoveDragOverFolder}
              onMoveDropOnFolder={onMoveDropOnFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FolderTreeNode;
